import { useEffect, useRef, useState } from 'react'
import PostalMime, { Email } from 'postal-mime';
import * as XLSX from 'xlsx';
import Spreadsheet from 'react-spreadsheet'
import MsgReader, { FieldsData } from '@kenjiuno/msgreader'
import * as DocxPreview from 'docx-preview';
import { pdfjs, Document, Page } from 'react-pdf';
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import axios from 'axios';
import { mergeRefs } from '@mantine/hooks';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export type Extension = 'eml' | 'xlsx' | 'docx' |  'pdf' | 'bpm' | 'jpg' | 'png' | 'jpeg' | 'pptx' | 'txt' | 'doc' | 'msg' | 'mp4'

export interface Excel {
  name: string;
  data: any[]
}

export interface Msg { msg:FieldsData }

export interface Ppt { ppt:string | null }

export interface Txt { txt:string }

type FilePreviewProps = {
    base64?: string;
    extension?: Extension
}

const FilePreview = ({base64, extension}:FilePreviewProps) => {
    const [content, setContent] = useState<Email | Excel[] | Ppt | Msg | Txt | null>(null);
    const [selected_index, setselected_index] = useState(0);
    const previewRef = useRef<HTMLDivElement>()
    const [numPages, setNumPages] = useState<number>();

    useEffect(()=>{
      setContent(null);
      switch (extension) {
        case 'eml':
          getEmail();
          break;
        case 'xlsx':
          getExcel();
          break;
        case 'docx':
        case 'doc':
          getDocx()
          break;
        case 'bpm':
          getBpm()
          break;
        case 'pptx':
          getPpt()
          break;
        case 'txt':
          getTxt()
          break;
        case 'msg':
          getMsg();
          break;
        default:
          break;
      }
      
    }, [base64])    

    const getEmail = async () => {
      try {
        const rawEmlContent = atob(base64 || '');
        const parser = new PostalMime();
        const email = await parser.parse(rawEmlContent)
        setContent(email)
      } catch (error) {
        console.error('Error getting email content:', error);
      }
    };

    const getExcel = async () => {
      try {

        const rawExcelContent = atob(base64 || '');
        const workbook = XLSX.read(rawExcelContent, { type: 'binary' });
        const sheetsData:any[] = [];
        workbook.SheetNames.forEach((sheetName:any) => {
          const worksheet = workbook.Sheets[sheetName];
          let data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          data = data.map((row:any)=>row.map((cell:any)=>({value:cell || '', readOnly:true})))
          sheetsData.push({ name: sheetName, data });
        });

        setContent(sheetsData)
      } catch (error) {
        console.error('Error getting excel content:', error);
      }
    }

    const getDocx = async () => {
      const rawDocxContent = atob(base64 || '');
      if(previewRef.current){
        DocxPreview.renderAsync(rawDocxContent, previewRef.current)
      } else { console.warn('Cant render docx, no ref') }
      
    }

    const getBpm = async () => {
      // No solution found
    }

    const getPpt = async () => {      
      try {
        let { data } = await axios.post(`https://fitdoc.herokuapp.com/upload`, {
          file: base64,
          filename: 'Preview.pptx'
        })
        if(data.errorcode!==0){ throw new Error(data.message) }
        setContent({ppt:data.file})
      } catch (error) {
        console.log(error)
        setContent({ppt:null})
      }
    }

    const getTxt = async () => {
      const byteCharacters = atob(base64 || '');
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/plain' });

      // Create Blob URI
      const blobURI = URL.createObjectURL(blob);
      setContent({txt:blobURI})
    }

    const getMsg = () => {
      try {
        const byteCharacters = atob(base64 || '');
        // Step 2: Convert byte array to ArrayBuffer
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const arrayBuffer = byteArray.buffer;

        const testMsg = new MsgReader(arrayBuffer);
        const msgInfo = testMsg.getFileData();

        setContent({msg:msgInfo})
      } catch (e){
        console.log('Error reading msg: ', e)
      }
    }

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
      setNumPages(numPages);
    }

    return (
    <div className='h-full'>
      {(extension === 'eml' && content && 'html' in content && content.html) && <div dangerouslySetInnerHTML={{__html:content.html}}></div>}
      {(extension === 'xlsx' && content && Array.isArray(content) && content[selected_index] && 'data' in content[selected_index]) && <div className='p-4'><div className='flex items-center gap-2 -translate-y-4'>{content.map((page,index)=>(<button onClick={()=>{setselected_index(index)}} key={`xlsx-${index}`} className={`${selected_index===index?'bg-blue-600 text-white':'bg-neutral-200 text-neutral-400'} text-sm font-medium px-2 py-2 rounded-b-md`}>{page.name}</button>))}</div><Spreadsheet className='text-sm' data={content[selected_index].data as any}/></div>}
      {(extension === 'docx' || extension === 'doc') && <div ref={mergeRefs(previewRef)}></div>}
      {(extension === 'pdf' && base64) && <Document file={`data:application/pdf;base64,${base64}`} onLoadSuccess={onDocumentLoadSuccess}>{Array.apply(null, Array(numPages)).map((x, i)=>i+1).map(page => <div key={`pdf-${page}`} className='mb-4 border shadow-sm last:mb-0 w-fit'><Page pageNumber={page}/></div>)}</Document>}
      {(extension === 'bpm' && base64) && <div className='flex flex-col items-center'><img className='opacity-75 w-[250px] h-auto' src={""} alt="No preview"/><p className='text-lg font-semibold'>Geen BPM-preview beschikbaar</p><p>Probeer uw diagram te exporteren als afbeelding</p><p className='text-sm text-neutral-400'>Beschikbare formaten: PNG, SVG, JPG</p></div>}
      {((extension === 'jpg' || extension === 'jpeg' || extension === 'png') && base64) && <img src={`data:image/png;base64,${base64}`}/>}
      {((extension === 'mp4') && base64) && <video autoPlay controls src={`data:video/mp4;base64,${base64}`}>The video-tag is not supported by your browser.</video>}
      {(extension === 'pptx' && content && 'ppt' in content) && <>{content.ppt ?<div className='w-full h-full bg-neutral-600'>  <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=https://cdn.qastan.be/Fitdoc/preview/${content.ppt}&embedded=true`} className='w-full h-full'/></div>:<div className='flex flex-col items-center'><img className='opacity-75 w-[250px] h-auto' src={""} alt="No preview"/><p className='text-lg font-semibold'>Geen PPT-preview beschikbaar</p><p>Probeer later opnieuw</p><p className='text-sm text-neutral-400'>U kan altijd uw Powerpoint exporteren als PDF</p></div>}</>}
      {(extension === 'txt' && content && 'txt' in content) && <iframe src={content.txt} className='w-full h-full'/>}
      {(extension === 'msg' && content && 'msg' in content) && <div><div className='py-4 mb-4 border-b'><p className='text-lg font-medium'>{content.msg.subject}</p><div className='flex items-center gap-2 my-2'><div className='flex items-center justify-center w-10 h-10 rounded-full select-none min-w-10 min-h-10 bg-neutral-100'><p className='text-lg font-bold opacity-30'>{content.msg.senderName?.charAt(0)}</p></div><div><p className='font-medium'>{content.msg.senderEmail}</p><p className='text-sm text-neutral-600'>{content.msg.senderName}</p></div></div><div className='flex items-center gap-1'><p className='text-sm text-neutral-600'>Aan:</p>{content.msg.recipients?.map((recipient, index)=>(<p key={index} className='text-sm font-medium'>{recipient.smtpAddress} {recipient.name}</p>))}</div></div><p className='whitespace-pre'>{content.msg.body}</p></div>}
    </div>
    )
}

export default FilePreview